import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import useTranslation from 'next-translate/useTranslation';
import Trans from 'next-translate/Trans';
import { ErrorBoundary } from 'react-error-boundary';

// Components
import ErrorAnalyticsFallback from '@/components/layouts/ErrorFallbacks/ErrorAnalyticsFallback';
import NoDataChart from '@/components/composite/Charts/components/NoDataChart';
import Icon from '@/components/base/Icon';

// Hooks
import useFormatNumber from '@/utils/hooks/format/useFormatNumber';
import useUser from '@/utils/hooks/swr/useUser';

const ChartWrapper = ({
    onReset = () => {
        /** */
    },
    className,
    isLoading = false,
    data,
    children,
    hasShadow = true,
    ...restProps
}) => {
    const { t } = useTranslation('analytics');
    const { user } = useUser({
        revalidateOnMount: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });
    const { formatNumber } = useFormatNumber(user?.settings?.numbers_format ?? 'comma');

    return (
        <ErrorBoundary FallbackComponent={ErrorAnalyticsFallback} onReset={() => onReset()}>
            <div
                className={clsx(
                    'flex h-full w-full flex-col rounded-md bg-white transition duration-200 dark:bg-icoDarkMode-anthracite',
                    className,
                    {
                        'hover:shadow-icoXL dark:hover:shadow-icoDarkXL': hasShadow,
                        'p-8': !data.noWrapper
                    }
                )}>
                {!isLoading && !data?.noWrapper && (data?.icon || data?.title || data?.subtitle) ? (
                    <ChartWrapper.Header icon={data?.icon} title={t(data?.title)}>
                        {data?.subtitle && (
                            <span className="mt-3 block text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                                <Trans
                                    i18nKey={`analytics:${data.subtitle}`}
                                    components={[
                                        <span key="total_likes" className="font-GSemiBold" />,
                                        <span
                                            key="previous_percentage"
                                            className={clsx('inline-flex items-center text-xs', {
                                                'text-icoRed': data?.value_evolution < 0,
                                                'text-icoGreen': data?.value_evolution > 0,
                                                'text-icoGray-400': data?.value_evolution === 0
                                            })}
                                        />
                                    ]}
                                    values={{
                                        totalLikes:
                                            data?.total_likes && data?.total_likes !== null
                                                ? data?.total_likes
                                                : 'N/A',
                                        previousPercentage:
                                            data?.value_evolution === null
                                                ? 'N/A'
                                                : data?.value_evolution !== 0
                                                  ? formatNumber(data?.value_evolution, {
                                                        ...data.data_format,
                                                        signDisplay: 'always'
                                                    })
                                                  : formatNumber(data?.value_evolution, {
                                                        ...data.data_format,
                                                        signDisplay: 'always'
                                                    }).replace('+', '=')
                                    }}
                                />
                            </span>
                        )}
                    </ChartWrapper.Header>
                ) : null}

                {isLoading ? (
                    <div className="flex h-full animate-pulse flex-col" data-testid="loading-data">
                        <span className="mb-5 mr-5 block w-1/2 rounded bg-icoGray-300 px-4 py-4 dark:bg-icoDarkMode-deepBlack" />
                        <span className="mr-5 block w-full flex-1 rounded bg-icoGray-300 px-4 py-[6.875rem] dark:bg-icoDarkMode-deepBlack" />
                        <div className="mt-4 flex justify-end">
                            <span className="block w-1/3 rounded-full bg-icoGray-300 px-4 py-3 dark:bg-icoDarkMode-deepBlack" />
                        </div>
                    </div>
                ) : data.graphData?.length > 0 ? (
                    <>
                        {children && (
                            <div className="flex w-full space-x-8">
                                {React.Children.map(children, (child) => {
                                    const newChild = React.cloneElement(child, {
                                        data,
                                        ...restProps
                                    });
                                    return newChild;
                                })}
                            </div>
                        )}
                    </>
                ) : (
                    <NoDataChart data={data} />
                )}
            </div>
        </ErrorBoundary>
    );
};

ChartWrapper.propTypes = {
    onReset: PropTypes.func,
    className: PropTypes.string,
    isLoading: PropTypes.bool,
    data: PropTypes.object,
    children: PropTypes.node
};

const Header = ({ children, icon, title }) => {
    return (
        <div className="mb-8">
            {(icon || title) && (
                <div className="flex items-center space-x-3">
                    <Icon icon={icon} className="text-icoGray-500 dark:text-icoDarkMode-wolf" />
                    <span>{title}</span>
                </div>
            )}
            {children}
        </div>
    );
};

Header.propTypes = {
    children: PropTypes.node,
    icon: PropTypes.string,
    title: PropTypes.string
};

ChartWrapper.Header = Header;

export default ChartWrapper;
